import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import uuid from 'uuid-random';

const MAX_FILES = 50;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const MIN_FILE_SIZE = 10 * 1024; // 10KB
const MIN_FILE_WIDTH = 480;
const MIN_FILE_HEIGHT = 480;
const MAX_FILE_WIDTH = 6000;
const MAX_FILE_HEIGHT = 6000;

const isTouchDevice = () => {
  return 'ontouchstart' in window;
};

const backendForDND = isTouchDevice() ? TouchBackend : HTML5Backend;

const createPicture = (file, multimediaTypeEnum) => ({
  file,
  angle: 0,
  multimediaTypeEnum,
  id: uuid(),
  error: file.error
});

const dropZoneConfig = Object.freeze({
  accept: 'image/*,.heic,.heif,.webp,.jfif',
  multiple: true
});

/**
 * Normaliza un archivo File asignando el MIME type correcto cuando está vacío.
 * Algunos archivos HEIC/HEIF del celular no tienen MIME type y el navegador
 * no puede identificarlos. Esta función lee la extensión y asigna el tipo correcto.
 *
 * @param {File} file - Archivo File del DOM (generalmente de un input o dropzone)
 * @returns {File} El archivo original si ya tiene MIME type válido, o un nuevo File con MIME type corregido
 *
 */
const normalizeFileWithMimeType = file => {
  // Si ya tiene MIME type válido, devolvermos tal como está
  if (file.type !== '') {
    return file;
  }

  // Obtener MIME type correcto por extensión
  const correctMimeType = getMimeTypeFromExtension(file.name);
  if (!correctMimeType) {
    return file; // Si no encontramos MIME type, devolvemos original
  }

  // Creamos nuevo File con MIME type correcto
  const normalizedFile = new File([file], file.name, {
    type: correctMimeType,
    lastModified: file.lastModified
  });

  // Preservamos errores existentes
  if (file.error) {
    normalizedFile.error = file.error;
  }

  return normalizedFile;
};

// Configuración de formatos soportados
const IMAGE_FORMATS = {
  extensions: ['jpg', 'jpeg', 'jfif', 'jpe', 'png', 'heic', 'heif', 'webp'],
  mimeTypes: ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'],
  mimeTypeMap: {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    jfif: 'image/jpeg',
    jpe: 'image/jpeg',
    png: 'image/png',
    heic: 'image/heif',
    heif: 'image/heif',
    webp: 'image/webp'
  }
};

const getFileExtension = filename =>
  filename
    ?.toLowerCase()
    .split('.')
    .pop();

const isValidImageByExtension = filename => {
  const extension = getFileExtension(filename);
  return IMAGE_FORMATS.extensions.includes(extension);
};

/**
 * Valida si un archivo es de un formato de imagen soportado.
 * Primero valida por MIME type, si no está disponible o no es válido,
 * hace fallback a validación por extensión de archivo.
 *
 * @param {File|null|undefined} file - Archivo File del DOM a validar
 * @returns {boolean} true si el archivo es de un formato soportado, false en caso contrario
 *
 */
const isValidImageFormat = file => {
  if (!file) return false;
  // Si tiene MIME type lo validamos, sino validamos por la extensión
  return IMAGE_FORMATS.mimeTypes.includes(file.type) || isValidImageByExtension(file.name);
};

const getMimeTypeFromExtension = filename => {
  const extension = getFileExtension(filename);
  return IMAGE_FORMATS.mimeTypeMap[extension] || '';
};

const keysImagesErrors = Object.freeze({
  format: 'professionalPubli.multimedia.image.errorFormat',
  sizeMin: 'professionalPubli.multimedia.image.errorSizeMin',
  sizeMax: 'professionalPubli.multimedia.image.errorSizeMax',
  dimensionsMin: 'professionalPubli.multimedia.image.errorDimensionsMin',
  dimensionsMax: 'professionalPubli.multimedia.image.errorDimensionsMax',
  generic: 'professionalPubli.multimedia.image.errorGeneric'
});

const keysPlansErrors = Object.freeze({
  format: 'professionalPubli.multimedia.plans.errorFormat',
  sizeMin: 'professionalPubli.multimedia.plans.errorSizeMin',
  sizeMax: 'professionalPubli.multimedia.plans.errorSizeMax',
  dimensionsMin: 'professionalPubli.multimedia.plans.errorDimensionsMin',
  dimensionsMax: 'professionalPubli.multimedia.plans.errorDimensionsMax',
  generic: 'professionalPubli.multimedia.plans.errorGeneric'
});

const resolveImageDimensions = file => {
  return new Promise(resolve => {
    const imageReader = new FileReader();
    const image = new Image();

    // Se maneja evento de carga exitosa
    image.onload = () =>
      resolve({
        file,
        dimensions: {
          width: image.width,
          height: image.height
        }
      });

    image.onerror = () => resolve({ file });
    imageReader.onerror = () => resolve({ file });

    // Se configura el encadenamiento de lectura
    imageReader.onload = () => {
      image.src = imageReader.result;
    };

    // Se inicia la lectura del archivo
    imageReader.readAsDataURL(file);
  });
};

const validateImageErrors = (file, dimensions, keysErrors) => {
  if (file.error) return file;

  // Validamos las dimensiones del archivo
  if (dimensions) {
    const { width, height } = dimensions;
    if (width > MAX_FILE_WIDTH || height > MAX_FILE_HEIGHT) {
      file.error = keysErrors.dimensionsMax;
      return file;
    }
    if (width < MIN_FILE_WIDTH || height < MIN_FILE_HEIGHT) {
      file.error = keysErrors.dimensionsMin;
      return file;
    }
  }
  // Validamos el tamaño de archivo
  if (file.size > MAX_FILE_SIZE) {
    file.error = keysErrors.sizeMax;
    return file;
  }
  if (file.size < MIN_FILE_SIZE) {
    file.error = keysErrors.sizeMin;
    return file;
  }

  return file;
};

export {
  backendForDND,
  MAX_FILE_HEIGHT,
  MAX_FILE_WIDTH,
  MIN_FILE_HEIGHT,
  MIN_FILE_WIDTH,
  MAX_FILES,
  MAX_FILE_SIZE,
  MIN_FILE_SIZE,
  createPicture,
  dropZoneConfig,
  keysImagesErrors,
  keysPlansErrors,
  resolveImageDimensions,
  validateImageErrors,
  isValidImageFormat,
  normalizeFileWithMimeType
};
